// Copyright (c) Ping. All rights reserved.
//
// Web wrapper around the shared `<Sidebar />`. Translates between web's
// `WebRoute` and the cross-platform `Destination` set, supplies web-only
// extras (Business Directory), and wires the persisted pin/reorder state
// from `uiPreferencesStore`.

import { useCallback } from 'react';

import { Briefcase } from 'lucide-react-native';

import { type Destination, type ExtraRailItem, Sidebar as SidebarUi, useUiPreferencesStore } from '@ping/ui';

import { useSelfProfile } from '@features/profile/selfProfileStore';

import { useRouter, type WebRoute } from './RouterContext';

const WEB_ROUTE_TO_DESTINATION: Partial<Record<WebRoute, Destination>> = {
  '/chat': 'chat',
  '/activity': 'activity',
  '/contacts': 'contacts',
  '/calls': 'calls',
  '/tasks': 'tasks',
  '/notes': 'notes',
  '/leaderboard': 'leaderboard',
  '/settings': 'settings',
};

const DESTINATION_TO_WEB_ROUTE: Record<Destination, WebRoute> = {
  chat: '/chat',
  activity: '/activity',
  contacts: '/contacts',
  calls: '/calls',
  tasks: '/tasks',
  notes: '/notes',
  leaderboard: '/leaderboard',
  settings: '/settings',
};

export function Sidebar(): JSX.Element {
  const router = useRouter();
  const accountName = useSelfProfile((s) => s.displayName) ?? '';
  const accountAvatarUri = useSelfProfile((s) => s.avatarUrl);

  // Routes outside the shared fixed-destination set (e.g. /business-directory,
  // which is the "Directory" tool) must NOT highlight a fixed rail item — map
  // them to 'settings' (never a rail item) so Chat doesn't falsely light up.
  // The Directory tool's own highlight comes from its `extras` entry's `active`.
  const current: Destination = WEB_ROUTE_TO_DESTINATION[router.route] ?? 'settings';

  // Persisted sidebar customisation.
  const pinnedItems = useUiPreferencesStore((s) => s.sidebarPinnedItems);
  const itemOrder = useUiPreferencesStore((s) => s.sidebarItemOrder);
  const setSidebarPinnedItems = useUiPreferencesStore((s) => s.setSidebarPinnedItems);
  const setSidebarItemOrder = useUiPreferencesStore((s) => s.setSidebarItemOrder);

  const handleNavigate = useCallback(
    (next: Destination): void => {
      router.push(DESTINATION_TO_WEB_ROUTE[next]);
    },
    [router],
  );

  const handleAccountPress = useCallback((): void => {
    router.openModal('settings');
  }, [router]);

  const handlePinItem = useCallback(
    (key: string): void => {
      if (!pinnedItems.includes(key)) {
        setSidebarPinnedItems([...pinnedItems, key]);
      }
    },
    [pinnedItems, setSidebarPinnedItems],
  );

  const handleUnpinItem = useCallback(
    (key: string): void => {
      setSidebarPinnedItems(pinnedItems.filter((k) => k !== key));
    },
    [pinnedItems, setSidebarPinnedItems],
  );

  const handleReorderItems = useCallback(
    (newOrder: readonly string[]): void => {
      setSidebarItemOrder(newOrder);
    },
    [setSidebarItemOrder],
  );

  const extras: ExtraRailItem[] = [
    {
      // Key must match the shared sidebar's tools-catalog entry so the
      // "Directory" tool routes to the Business Directory page.
      key: 'directory',
      icon: Briefcase,
      customIconName: 'directory',
      accessibilityLabel: 'Directory',
      active: router.route === '/business-directory',
      onPress: () => router.push('/business-directory'),
    },
  ];

  return (
    <SidebarUi
      current={current}
      onNavigate={handleNavigate}
      accountName={accountName}
      {...(accountAvatarUri !== null ? { accountAvatarUri } : {})}
      onAccountPress={handleAccountPress}
      extras={extras}
      pinnedItems={pinnedItems}
      itemOrder={itemOrder}
      onPinItem={handlePinItem}
      onUnpinItem={handleUnpinItem}
      onReorderItems={handleReorderItems}
    />
  );
}
